import { FC, MouseEventHandler, ReactNode } from 'react';

import { useComponentId } from '@telialt/utils/src/hooks';

import { AllBlockTypes } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import { isContentItemLeadForm } from '../../utils/typeGuards';
import { CtaData, Image } from '../common/model';
import { useLeadFormModalControls } from '../LeadForm/components/LeadFormModal/useLeadFormModalControls';
import { LeadForm } from '../LeadForm/LeadForm';

type RenderParams = {
    id: string;
    text?: string;
    href?: string;
    target?: '_blank';
    rel?: string;
    image?: Image;
    onClick?: MouseEventHandler;
};
type RenderFunction = (params: RenderParams) => ReactNode;

type Props = {
    cta: CtaData;
    children: RenderFunction;
};

export const CtaProvider: FC<Props> = ({ cta, children: renderChildren }) => {
    const leadFormModalUid = useComponentId(cta?.referencedContent?.sys?.id);
    const { openLeadFormModal } = useLeadFormModalControls(leadFormModalUid);

    const renderParams: RenderParams = {
        id: cta.id,
        text: cta.title,
        image: cta.image,
    };
    let renderedReferencedContent: ReactNode = null;

    if (cta.referencedContent) {
        if (isContentItemLeadForm(cta.referencedContent) && cta.referencedContent.fields.formType === 'MODAL') {
            renderParams.onClick = (event) => openLeadFormModal(null, event?.currentTarget as HTMLElement);
            renderedReferencedContent = <LeadForm contentItem={cta.referencedContent} modalUid={leadFormModalUid} />;
        } else {
            const blockId = getBlockId(cta.referencedContent as AllBlockTypes);
            renderParams.href = `#${blockId}`;
        }
    } else {
        renderParams.href = cta.url;
        renderParams.target = cta.target;
        renderParams.rel = cta.rel;
    }

    const renderedContent = renderChildren(renderParams);

    return (
        <>
            {renderedContent}
            {renderedReferencedContent}
        </>
    );
};
